import React, { createContext, useContext, useState, useEffect, type ReactNode } from "react";

type Locale = string;
type Dict = Record<string, any>;

interface I18nContextType {
  locale: Locale;
  setLocale: (l: Locale) => void;
  t: (key: string, options?: { returnObjects?: boolean; fallback?: string }) => string | any;
}

const I18nContext = createContext<I18nContextType>({
  locale: "en",
  setLocale: () => {},
  t: (k) => k,
});

const supportedLocales = ["en", "pl"]; // List of your available languages

export const I18nProvider = ({ children }: { children: ReactNode }) => {
  // pick browser language or fallback to 'en'
  const browserLocale = navigator.language.slice(0, 2).toLowerCase();
  const initialLocale = supportedLocales.includes(browserLocale) ? browserLocale : "en";

  const [locale, setLocaleState] = useState<Locale>(initialLocale);
  const [dict, setDict] = useState<Dict>({});

  const load = async (l: Locale) => {
    if (dict[l]) return;
    const res = await fetch(`/assets/languages/${l}.json`);
    const json = await res.json();
    setDict((prev) => ({ ...prev, [l]: json }));
  };

  useEffect(() => {
    load(locale);
  }, [locale]);

  const setLocale = (l: Locale) => {
    setLocaleState(l);
    load(l);
  };

  const t = (key: string, options?: { returnObjects?: boolean; fallback?: string }) => {
    const value = key.split(".").reduce((o, k) => o?.[k], dict[locale]);
    if (value === undefined) return options?.fallback ?? key;
    if (options?.returnObjects) return value;
    return typeof value === "string" ? value : key;
  };

  return <I18nContext.Provider value={{ locale, setLocale, t }}>{children}</I18nContext.Provider>;
};

export const useI18n = () => useContext(I18nContext);
